// FP 보정 간트 — 재배치 규칙(순수 함수 모듈)
//
// old TreeGrid의 `GanttRunAdjust:'Shrink'` / `GanttRunError:'Sort'` / `GanttRunJoined:'4'` 를
// **절대시각 모델**로 옮긴 것이다. old는 행을 [박스, 빈공간, 박스, …] 시퀀스로 들고 있어서
// "빈 공간이 줄어든다"는 표현이 필요했지만(그래서 `activityToRunBox`가 갭 자리에 `{}`를 끼웠다),
// 우리 박스는 `s`/`e`가 절대시각이라 빈 공간을 저장할 필요가 없고 단일 스윕으로 같은 결과가 나온다.
// 근거·검증은 reports/gantt-implementation.md §6-3.
//
// 이 파일에는 React도 캔버스도 없다 — 좌표/시각 계산만 한다.

// 이동·리사이즈 단위 — old `GanttDragUnits: 'h'`. 드래그·버튼·리사이즈가 **모두 이 값 하나**를 쓴다
// (2026-08-28 사용자 결정: 이동 단위 선택 field를 없애고 1시간으로 통일. ×2 버튼만 2시간).
export const SNAP_MS = 60 * 60 * 1000;

/**
 * ⚠️ **위치가 아니라 변위(Δ)를 스냅한다** — old §6-2가 `Math.round(Δt / 3600000)`로 적어둔 규칙이고,
 * 초기 구현이 `Math.round(위치 / 1h)`로 어긋나 있던 것을 2026-08-28에 정정했다.
 *
 * 위치를 스냅하면 결과가 항상 정시(:00)가 되어 **첫 조작에서 원본의 분·초·ms가 통째로 버려진다**
 * (`13:37:20.450`을 살짝만 끌어도 `14:00:00.000`). 계획 엔진이 계산한 시각을 조작이 훼손하는 셈이고,
 * 드래그(위치 스냅)와 버튼 이동(변위 이동)의 결과가 갈리는 원인이기도 했다.
 *
 * 그래서 항상 **기준점(anchor)에서 snap의 배수만큼** 떨어진 격자에 놓는다. 정확히 3,600,000ms의
 * 배수만 더하므로 1시간 미만 나머지(분·초·ms)가 비트 단위로 보존된다.
 */
export const snapTs = (delta, snap = SNAP_MS) => Math.round(delta / snap) * snap;

/** anchor 기준 격자에서 `value` **이하** 최대 격자점. 하한을 clamp할 때 쓴다(격자를 벗어나지 않게). */
const latticeFloor = (anchor, value, snap) => anchor + Math.floor((value - anchor) / snap) * snap;

/** anchor 기준 격자에서 `value` **이상** 최소 격자점. */
const latticeCeil = (anchor, value, snap) => anchor + Math.ceil((value - anchor) / snap) * snap;

// JC(작업교체) 박스는 백엔드가 본 활동 앞뒤로 끼워 넣은 합성 활동이고 id가 `<본id>_PREVJC`/`_NEXTJC`다.
// old `GanttRunJoined:'4'` = "joined 박스는 조정 시 함께 처리" → **그룹을 하나의 강체로 취급**한다.
export const groupKeyOf = (id) => String(id ?? "").replace(/_(PREV|NEXT)JC$/, "");

/** JC 의 **순수 소요** — `jc_time`(마스터 값)이 있으면 그것, 없으면 현재 span(옛 데이터 방어). */
const jcLengthOf = (box) => (Number.isFinite(box.jcTime) ? box.jcTime : box.e - box.s);

/**
 * 그룹을 **압축형**(JC 가 활동에 딱 붙은 형태)으로 만든다 — §7-51.
 *
 * ⚠️ **떨어진 JC 는 unit 을 통째로 부풀린다.** `prev JC — 휴무 — 활동` 배치에서는
 * `min(s)~max(e)` 가 휴무를 통째로 품어 **실제 바 11h 짜리가 68h 짜리 덩어리**가 된다.
 * 그 길이로 이웃을 밀면 **밴드 앞에 있던 작업들이 밴드 뒤로 날아가고**(2026-09-16 신고),
 * `delta = newStart - unit.s` 도 JC 기준이라 **활동이 드롭 지점에서 60h 떨어진 곳**에 놓인다.
 *
 * 그 틈은 **지금 좌표에서** 엔진이 예약한 것이고(`FreeTimeSectionSupplier:343`),
 * 옮기고 나면 JC 는 `applyDivideRules` 가 **활동에서 다시 파생**한다(묶음 D). 즉 **이동 판정에
 * 들고 다닐 값이 아니다.** 여기서 미리 접어 두면 길이와 기준점이 한 번에 맞는다.
 *
 * ⚠️ **활동 박스는 손대지 않는다.** 활동이 스스로 휴무를 품은 span 은 진짜 점유다.
 * JC 가 없거나 이미 붙어 있으면 **입력을 그대로 돌려준다** — JC 없는 행은 수치가 한 톨도 안 바뀐다.
 */
export const compactGroup = (groupBoxes = []) => {
  const main = groupBoxes.find(box => box.type !== "jc");
  const jcBoxes = groupBoxes.filter(box => box.type === "jc");
  if (!main || jcBoxes.length === 0 || jcBoxes.length + 1 !== groupBoxes.length) {
    return groupBoxes;                                  // 활동 1 + JC 0~2 형태가 아니면 손대지 않는다
  }

  const prev = jcBoxes.find(box => box.jcKind === "prev");
  const next = jcBoxes.find(box => box.jcKind === "next");
  if (jcBoxes.length !== (prev ? 1 : 0) + (next ? 1 : 0)) return groupBoxes;

  const packed = [];
  if (prev) packed.push({ ...prev, s: main.s - jcLengthOf(prev), e: main.s });
  packed.push(main);
  if (next) packed.push({ ...next, s: main.e, e: main.e + jcLengthOf(next) });

  const same = packed.every((box, i) => box.s === groupBoxes[i]?.s && box.e === groupBoxes[i]?.e);
  return same ? groupBoxes : packed;                    // 이미 붙어 있으면 원본 그대로(참조 보존)
};

/**
 * 행의 박스 배열 → **이동 단위(unit)** 배열. JC 그룹은 한 덩어리로 묶인다.
 * 각 unit은 `{ key, s, e, boxes }`이고 `s` 오름차순이다(박스 배열이 이미 정렬돼 있다는 전제).
 * 내부 간격은 보존한다(R2 — 그룹 = 하나의 박스로 취급).
 *
 * ⚠️ 단, **떨어진 JC 는 활동에 붙여 놓고 잰다**(`compactGroup`, §7-51). R2 의 "내부 간격 보존"은
 * 활동끼리의 간격 이야기지 **파생값인 JC 와의 틈**이 아니다.
 */
export const buildUnits = (boxes = []) => {
  const byKey = new Map();

  boxes.forEach(box => {
    const key = groupKeyOf(box.id);
    const unit = byKey.get(key);
    if (unit) unit.boxes.push(box);
    else byKey.set(key, { key, boxes: [box] });
  });

  return [...byKey.values()]
    .map(unit => {
      const packed = compactGroup(unit.boxes);
      return {
        key: unit.key,
        s: Math.min(...packed.map(box => box.s)),
        e: Math.max(...packed.map(box => box.e)),
        boxes: packed
      };
    })
    .sort((a, b) => a.s - b.s);
};

/**
 * unit 배열 → 박스 배열(원래 모양). 이동으로 생긴 delta를 각 박스에 그대로 얹는다.
 *
 * **`homeS`/`homeE` = 이 스윕이 밀기 **전**의 좌표.** `applyDivideRules` 가 규칙 적용 후 박스를
 * 되당길 때 **여기보다 앞으로는 못 가게** 막는 바닥이다(§7-29c). 밀려난 만큼만 되돌아올 수 있다.
 * `netMs` 와 같은 부류의 **선택적 부가 정보**이고, 매 스윕마다 그때의 좌표로 덮어써진다.
 *
 * ⚠️ **`movingKey` 의 unit 에는 붙이지 않는다**(2026-09-11, §7-41).
 * `homeS` 의 뜻은 "**밀려나기** 전 자리"인데, 끌고 있는 그룹은 밀려난 게 아니라 **사용자가 옮긴** 것이라
 * 그 전제가 성립하지 않는다. 붙이면 바닥이 걸려 **왼쪽으로 옮길 때 자기 JC 가 원래 자리에 남는다**
 * (그룹 안 JC 는 활동과 늘 맞닿아 `abutting` 이라 항상 바닥을 탄다).
 * 앵커 활동 자신은 루프 첫 박스라 `abutting` 이 `false` 여서 증상이 안 났고, 그래서 JC 도입 전까지
 * 드러나지 않았다. 값이 없으면 `Number.isFinite` 가드가 ∓Infinity(= 제약 없음)로 떨어뜨린다.
 *
 * @param movingKey 끌고 있는 unit 의 key(`groupKeyOf`). 생략하면 전부에 붙인다(옛 동작).
 */
export const unitsToBoxes = (units = [], movingKey = null) => units
  .flatMap(unit => unit.boxes.map(box => {
    const moved = unit.delta
      ? { ...box, s: box.s + unit.delta, e: box.e + unit.delta }
      : { ...box };
    return unit.key === movingKey ? moved : { ...moved, homeS: box.s, homeE: box.e };
  }))
  .sort((a, b) => a.s - b.s);

/**
 * 이동 대상을 뺀 나머지 unit 사이의 **빈 구간** 목록.
 * 앞뒤로 계획 구간 경계까지의 열린 구간도 포함한다(마지막 박스 뒤 = `bound.endTs`까지).
 */
export const buildGaps = (units = [], movingKey = null, bound = null) => {
  const rest = units.filter(unit => unit.key !== movingKey);
  const lo = bound?.startTs ?? -Infinity;
  const hi = bound?.endTs ?? Infinity;

  const gaps = [];
  let cursor = lo;

  rest.forEach(unit => {
    if (unit.s > cursor) gaps.push({ s: cursor, e: unit.s });
    cursor = Math.max(cursor, unit.e);
  });
  if (hi > cursor) gaps.push({ s: cursor, e: hi });

  return gaps;
};

export const DROP_FREE = "free";       // 갭이 충분 — 밀림 없음
export const DROP_PUSH = "push";       // 갭이 좁음 — 뒤쪽이 밀린다(§6-3 스윕)
export const DROP_BLOCKED = "blocked"; // 박스 몸통 위 / 계획구간 밖 — 드롭 불가

/**
 * 커서 시각 → 드롭 판정 3단계(§6-5a).
 *
 * - **박스 경계(seam) 흡착**: 경계 근처면 그 지점에 삽입한다(뒤는 밀린다) ← 최우선
 * - 박스 몸통 위: **금지**(old 컴포넌트도 드래그 드롭 시 몸통 드롭을 막았다 — 2026-08-28 사용자 확인)
 * - 놓은 자리에 다 들어가면: 밀림 없음 / 뒤쪽과 겹치면: 놓은 자리에 두고 뒤를 민다(§6-3 스윕)
 *
 * ⚠️ **경계 흡착이 몸통 판정보다 먼저다**(2026-08-28 사용자 지적으로 추가). 박스가 딱 붙어 있으면
 * (선행 종료 == 후속 시작) 그 사이 갭이 **0폭**이라 경계가 "몸통 위"로 판정돼 **붙어 있는 작업들
 * 사이에는 아무것도 끼워 넣을 수 없었다.** 실제 계획 데이터는 연속 배치가 절반 이상이라 이 경우가
 * 예외가 아니라 기본이다. `seamTolerance`(화면이 px → 시각으로 환산해 넘긴다) 안에 들어오면 그
 * 경계에 **정확히** 스냅한다(1시간 격자 스냅을 적용하지 않는다 — 붙이기는 정확해야 한다,
 * old `attachActivity`도 `target.End`를 그대로 썼다).
 *
 * ⚠️ **놓을 위치를 `gap.e - duration`으로 clamp하면 안 된다**(2026-08-28 단위 테스트로 발견).
 * 그렇게 하면 박스를 오른쪽으로 끌 때 뒤 박스에 닿는 순간 멈출 뿐이라 **밀기가 영영 발동하지 않는다**.
 * 갭 시작보다 앞으로는 못 가게 막되(그 앞은 선행 박스 몸통이다) 뒤로는 열어 두고 스윕에 맡긴다.
 *
 * `grabOffset`은 **커서를 그룹의 어느 지점에 대응시킬지**를 호출부가 정하는 값이다 (`target = ts - grabOffset`).
 * 원래 뜻은 "박스의 어디를 집었는가"(`ts - box.s`)로, 잡은 지점의 상대 위치를 보존해 박스가
 * 커서로 순간이동하지 않게 하는 것이었다. **지금 보정 간트는 이동 제스처에 `prev JC 길이`를
 * 넘겨 커서를 곧 활동 시작에 맞춘다**(§7-53) — 이 제스처는 바를 옮겨 그리지 않고 자리표시만
 * 하므로 순간이동이 없고, 휴무로 늘어난 긴 바에서 겨누기가 훨씬 쉬워진다.
 *
 * ⚠️ **모든 판정을 `target`(= 박스가 놓일 시작시각) 하나로 한다** — 2026-08-28 정정.
 * 예전에는 흡착·몸통 판정이 **커서(`ts`)**를, 위치 계산만 `ts - grabOffset`을 써서 기준이 둘이었다.
 * 그러면 **어디를 잡았느냐가 결과를 바꾼다**: 박스를 가운데서 잡으면 커서와 박스 왼쪽 끝이
 * `grabOffset`만큼 벌어지는데, 흡착은 커서만 보므로 사용자가 **박스가 아니라 커서를 목표 지점에
 * 조준**해야 했다(실제 증상: "1·2 사이에 넣으려면 2번 앞이 아니라 1번 쪽에 갖다놔야 한다").
 * 흡착이 걸리는 순간 바가 `grabOffset`만큼 툭 튀는 것도 같은 원인이다.
 *
 * 몸통 금지 판정도 같이 옮겼다. 커서 기준이던 옛 주석("사용자가 가리키는 곳이 판정 대상")은
 * **실이동(§7-15d)으로 바뀐 뒤 전제가 깨졌다** — 화면에서 움직이는 건 커서가 아니라 박스다.
 */
export const resolveDrop = ({
  units = [],
  movingKey = null,
  duration = 0,
  ts = null,
  grabOffset = 0,
  bound = null,
  snap = SNAP_MS,
  seamTolerance = 0,
  // 변위 스냅의 **기준점** = 드래그를 시작한 작업의 원래 시작시각. 이 값의 분·초·ms가 그대로
  // 보존된다. 0이면 정시 격자(옛 동작)가 되므로 화면은 반드시 넘겨야 한다.
  originStart = 0
}) => {
  if (ts == null || !Number.isFinite(ts)) return { status: DROP_BLOCKED, start: null, gap: null };

  const others = units.filter(unit => unit.key !== movingKey);
  // **박스가 놓일 시작시각.** 아래 세 판정(흡착·몸통·갭)이 전부 이 값을 본다 — 그래야 "어디를
  // 잡았느냐"가 결과에 영향을 주지 않는다(직접 조작의 기본).
  const target = ts - grabOffset;

  // ① 경계 흡착 — **박스 왼쪽 끝**이 어느 경계 근처면 거기에 삽입한다.
  let start = null;
  if (seamTolerance > 0) {
    let best = null;
    others.forEach(unit => {
      [unit.s, unit.e].forEach(seam => {
        const distance = Math.abs(target - seam);
        if (distance <= seamTolerance && (best === null || distance < best.distance)) {
          best = { seam, distance };
        }
      });
    });
    if (best) start = best.seam;
  }

  // ② 경계가 아니면 **박스 시작점이 다른 작업 몸통 위인지**를 본다.
  let gap = null;
  if (start === null) {
    if (others.some(unit => target >= unit.s && target <= unit.e)) {
      return { status: DROP_BLOCKED, start: null, gap: null };
    }

    gap = buildGaps(units, movingKey, bound).find(candidate => target >= candidate.s && target <= candidate.e);
    if (!gap) return { status: DROP_BLOCKED, start: null, gap: null };

    // 놓일 시작시각을 **원래 시작 기준 1시간 격자**에 올린다(분·초·ms 보존, §7-15e).
    // 스냅 결과가 갭 시작보다 앞이면 갭 시작에 정확히 붙는다(붙이기 계열 — 격자를 벗어나는 유일한 곳).
    start = Math.max(originStart + snapTs(target - originStart, snap), gap.s);
  }

  // ③ 계획 구간(조회 축) 밖으로 나가면 거부 — old `checkOutBound` → FP_MSG_OUT_PLAN_BOUND (R3).
  if (bound && (start < bound.startTs || start + duration > bound.endTs)) {
    return { status: DROP_BLOCKED, start: null, gap };
  }

  // ④ 놓인 구간이 다른 작업과 겹치면 밀림이다. **갭 끝 비교가 아니라 실제 겹침으로 판정**한다 —
  //    경계 흡착으로 들어온 경우엔 애초에 갭이 없다.
  const end = start + duration;
  const overlaps = others.some(unit => unit.s < end && unit.e > start);
  return { status: overlaps ? DROP_PUSH : DROP_FREE, start, gap };
};

/**
 * `Shrink` 재배치 — 이동 후 겹침을 **단일 스윕**으로 해소한다(§6-3).
 *
 * 한 루프가 문서의 세 동작을 동시에 만족한다:
 *   밀기(`u.s < cursor`일 때만) / 빈 공간 흡수(갭이 밀림량보다 크면 뒤 박스가 안 움직인다) /
 *   Sort(정렬 순회라 결과가 항상 start 오름차순).
 *
 * 반환은 `{ units, pushedKeys }`이고, 계획 구간을 넘기면 `null`(= 전체 거부, R3).
 * 입력 unit은 건드리지 않는다 — 미리보기(드래그 중)와 확정(드롭)이 같은 함수를 쓴다.
 */
export const applyShrink = ({ units = [], movingKey = null, newStart = 0, planEndTs = Infinity }) => {
  const moving = units.find(unit => unit.key === movingKey);
  if (!moving) return null;

  const seq = units
    .map(unit => (
      unit.key === movingKey
        ? { ...unit, s: newStart, e: newStart + (unit.e - unit.s), delta: newStart - unit.s }
        : { ...unit, delta: 0 }
    ))
    // ⚠️ **동시 시작(tie)이면 이동 대상이 앞에 온다** — 2026-08-28 수정.
    // 경계에 정확히 붙이면(흡착) 이동 박스와 이웃의 시작이 **같아진다**. `sort`는 안정 정렬이라
    // 그때 **원래 배열 순서**가 이기는데, 옮기려는 작업이 이웃보다 오른쪽에 있었으면 이웃이 먼저
    // 처리돼 **이동 박스가 자기 자리에서 밀려난다**(caret은 경계를 가리키는데 결과는 이웃 뒤).
    // 원래 위치가 결과를 바꾸는 셈이고, `resolveDrop`이 내린 "밀림" 판정과도 어긋났다.
    // 사용자가 그 시작 시각을 **명시적으로 지정**했으므로 그 자리를 차지하고 나머지를 민다.
    .sort((a, b) => (a.s - b.s) || (a.key === movingKey ? -1 : b.key === movingKey ? 1 : 0));

  const pushedKeys = new Set();
  let cursor = -Infinity;

  seq.forEach(unit => {
    if (unit.s < cursor) {
      const shift = cursor - unit.s;
      unit.s += shift;
      unit.e += shift;                    // duration 보존
      unit.delta += shift;
      if (unit.key !== movingKey) pushedKeys.add(unit.key);
    }
    cursor = unit.e;
  });

  const last = seq[seq.length - 1];
  if (last && last.e > planEndTs) return null;

  return { units: seq, pushedKeys };
};

/**
 * `Slide` 재배치 — old `GanttRunMoveRight:'Slide'`(우클릭 드래그) 이식.
 * 문서 정의: *"Shifts box left/right **within its row only**"*.
 *
 * `applyShrink`(좌드래그)와 **세 가지가 다르다**:
 *  1. **행을 벗어나지 않는다** — 자원 재배정이 아니라 같은 자원 안에서의 시간 조정이다.
 *  2. **순서가 바뀌지 않는다** — 원래 순서 배열을 고정해 두고 그 위에서만 민다. `applyShrink`처럼
 *     시작 시각으로 다시 정렬하면 이웃을 뛰어넘는 순간 순서가 뒤집힌다.
 *  3. **양쪽으로 민다** — 오른쪽으로 밀면 뒤쪽이, 왼쪽으로 밀면 **앞쪽이** 밀린다. `applyShrink`는
 *     뒤로만 밀기 때문에 왼쪽 슬라이드를 표현할 수 없다(그래서 함수를 나눴다).
 *
 * 공통점은 "빈 공간을 먼저 먹고, 다 먹으면 이웃을 민다"는 것이다 — 두 방향 모두 갭이 밀림량보다
 * 크면 이웃이 움직이지 않고 갭만 줄어든다.
 *
 * 반환은 `{ units, pushedKeys }`, 계획 구간을 벗어나면 `null`(전체 거부, R3).
 */
export const applySlide = ({ units = [], movingKey = null, newStart = 0, bound = null }) => {
  const index = units.findIndex(unit => unit.key === movingKey);
  if (index === -1) return null;

  // **정렬하지 않는다** — 입력 순서(= 원래 시작 시각 오름차순)가 곧 지켜야 할 순서다.
  const seq = units.map((unit, i) => {
    const duration = unit.e - unit.s;
    return i === index
      ? { ...unit, s: newStart, e: newStart + duration, delta: newStart - unit.s }
      : { ...unit, delta: 0 };
  });

  const pushedKeys = new Set();

  // 뒤쪽(오른쪽)으로 밀기 — 갭이 남으면 거기서 멈춘다.
  let cursor = seq[index].e;
  for (let i = index + 1; i < seq.length; i += 1) {
    if (seq[i].s < cursor) {
      const shift = cursor - seq[i].s;
      seq[i].s += shift;
      seq[i].e += shift;
      seq[i].delta += shift;
      pushedKeys.add(seq[i].key);
    }
    cursor = seq[i].e;
  }

  // 앞쪽(왼쪽)으로 밀기 — 위와 대칭이다.
  cursor = seq[index].s;
  for (let i = index - 1; i >= 0; i -= 1) {
    if (seq[i].e > cursor) {
      const shift = seq[i].e - cursor;
      seq[i].s -= shift;
      seq[i].e -= shift;
      seq[i].delta -= shift;
      pushedKeys.add(seq[i].key);
    }
    cursor = seq[i].s;
  }

  if (bound) {
    const first = Math.min(...seq.map(unit => unit.s));
    const last = Math.max(...seq.map(unit => unit.e));
    if (first < bound.startTs || last > bound.endTs) return null;
  }

  return { units: seq, pushedKeys };
};

/**
 * 이미 계획 구간(조회 축) 밖으로 나가 있는 행을 위해 경계를 **현재 상태까지** 넓힌다.
 *
 * ⚠️ 조회 구간을 좁혀서 보면 축 끝에서 잘린 작업이 그대로 남는다. 그 상태를 위반으로 보면
 * **그 행은 아무 보정도 못 한다** — 앞쪽에 빈 자리가 남아 있어도 마찬가지다. 클램프가
 * "현재 위치는 항상 가능"을 전제로 이분 탐색을 하는데 그 기준점부터 불가 판정이 나서
 * 전부 거부로 떨어지기 때문이다(2026-09-09 사용자 신고).
 *
 * **새로 더 나가는 것만 막고, 이미 나가 있던 만큼은 허용한다** — 넓히는 폭이 현재 양 끝까지라
 * 전체 구간이 지금보다 커질 수는 없다.
 */
export const relaxBound = (bound, items = []) => {
  if (!bound || items.length === 0) return bound;
  return {
    ...bound,
    startTs: Math.min(bound.startTs, ...items.map(item => item.s)),
    endTs: Math.max(bound.endTs, ...items.map(item => item.e))
  };
};

/**
 * **거부하지 말고 갈 수 있는 데까지만 간다** — 벽에 붙이는 클램프.
 *
 * `applyShrink`/`applySlide`는 제약(계획 구간, 분할타입)에 걸리면 `null`을 돌려주고, 호출부는
 * 그걸 "이동 취소"로 처리해 박스가 **원래 자리로 튕겨 돌아갔다**. 조금이라도 움직일 수 있는
 * 상황에서도 통째로 되돌아가니 밀기 조작이 벽 근처에서 아예 안 먹는 것처럼 보였다
 * (2026-09-08 사용자 요청: "튕겨서 돌아오지 말고 갈 수 있는 데까지만 밀고 막아줘").
 *
 * 가능/불가는 이동 방향에 대해 **단조**다(더 밀수록 벽에 가까워질 뿐 되살아나지 않는다).
 * 그래서 현재 위치(항상 가능)와 요청 위치 사이를 이분 탐색하면 ms 단위로 정확한 한계점이 나온다.
 * 스냅 격자를 벗어나지만 그게 맞다 — 벽에 정확히 붙는 게 목적이다(`resolveDrop`의 갭 흡착과 같은 계열).
 *
 * ⚠️ **이분 탐색은 비싸다 — 벽에 닿은 채로 끌면 마우스무브마다 `run`을 25회쯤 부른다**(ms 해상도라
 * 1시간 범위를 좁히는 데 22회). `run`은 행 전체 재배치라 박스가 많은 행에서 그대로 지연이 된다
 * (2026-09-10 신고: "특정 행에서 왼쪽 리사이즈만 느리다" — 600박스 행에서 프레임당 14ms).
 *
 * 그래서 `memo`를 받는다. **한 번의 드래그 동안 `run`은 `start`만의 순수 함수**다(행·`from`·휴무·
 * 경계가 전부 고정). 그러니 한 번 찾은 벽은 그 드래그 내내 유효하다:
 *   - 요청이 **이미 아는 벽 너머**면 → `run` 호출 **0회**로 캐시된 결과를 그대로 돌려준다
 *   - 요청이 **아는 가능 구간 안**이면 → 직행 1회로 끝난다(원래도 그랬다)
 *   - 그 사이면 → 알고 있는 `ok`/`ng`로 **범위를 좁혀** 탐색한다
 * 호출부는 드래그 시작마다 새 객체(`{}`)를 주면 된다. 안 주면 옛 동작 그대로다.
 *
 * @param run    `(start) => 결과 | null` — 제약 검사까지 포함한 배치 함수
 * @param from   현재 시작시각(가능하다고 전제하는 기준점)
 * @param wanted 사용자가 요청한 시작시각
 * @param memo   드래그 1회 동안 유지되는 캐시(선택). **행이 바뀌면 새로 만들어야 한다.**
 * @returns `{ result, start, clamped }`, 또는 현재 위치조차 불가하면 `null`
 */
export const runClamped = (run, from, wanted, memo = null) => {
  const dir = Math.sign(wanted - from);
  // 방향·기준점이 달라지면 캐시는 무효다(같은 드래그라도 좌우로 왔다 갔다 할 수 있다).
  const cache = memo && memo.from === from && memo.dir === dir ? memo : null;

  // 이미 아는 벽 너머 — 답은 항상 그 벽이다. `run` 을 한 번도 부르지 않는다.
  if (cache && cache.ng != null && (wanted - cache.ng) * dir >= 0) {
    return { result: cache.result, start: cache.ok, clamped: true };
  }

  const direct = run(wanted);
  if (direct) {
    // ⚠️ `ok`/`result` 는 **가장 먼 가능 지점**이어야 한다. 최신 값으로 덮으면, 벽을 찾아 둔 뒤
    // 가까운 데를 한 번 들렀을 때 그 가까운 자리가 "벽"이 되어 이후 요청이 거기로 잘린다.
    const keep = cache && cache.ok != null && (cache.ok - wanted) * dir > 0;
    if (memo) {
      Object.assign(memo, {
        from,
        dir,
        ok: keep ? cache.ok : wanted,
        result: keep ? cache.result : direct,
        ng: cache?.ng ?? null
      });
    }
    return { result: direct, start: wanted, clamped: false };
  }

  // 아는 만큼 구간을 좁혀서 시작한다. 없으면 옛날처럼 `from` 부터.
  let ok = cache && cache.ok != null ? cache.ok : from;
  let best = cache && cache.ok != null && cache.result ? cache.result : run(ok);
  if (!best) {
    if (ok === from) return null;    // 손댈 수 없는 상태 — 호출부가 기존대로 거부한다
    ok = from;
    best = run(from);
    if (!best) return null;
  }

  let ng = cache && cache.ng != null && (cache.ng - wanted) * dir <= 0 ? cache.ng : wanted;
  while (Math.abs(ng - ok) > 1) {
    const mid = ok + Math.trunc((ng - ok) / 2);
    const attempt = run(mid);
    if (attempt) {
      ok = mid;
      best = attempt;
    } else {
      ng = mid;
    }
  }

  if (memo) Object.assign(memo, { from, dir, ok, ng, result: best });
  return { result: best, start: ok, clamped: true };
};

// ── 엣지 드래그 리사이즈 ───────────────────────────────────────────────────────
// old `GanttRunResize:'Resize'` + `GanttResizeDelete:'0'`(0폭 삭제 금지 = 최소 길이 하한).
// 리사이즈는 **한쪽 끝만** 움직인다 — 반대쪽 끝과 duration 중 무엇이 고정인지가 이동과 다르다.

export const RESIZE_LEFT = "left";
export const RESIZE_RIGHT = "right";

// 최소 작업 길이 — old는 "0폭 금지"만 정했고 값은 안 줬다. 드래그 스냅 단위와 같게 두면
// **스냅 한 칸이 곧 최소 길이**가 되어 규칙이 하나로 설명된다.
export const MIN_DURATION_MS = SNAP_MS;

/**
 * JC 그룹 박스 배열 → 한쪽 끝을 `edgeTs`로 옮긴 **새 박스 배열**(원본 불변).
 *
 * JC 띠 자체는 리사이즈 대상이 아니다(old 박스 `Type:'Solid'` = 이동만 가능). 본 활동의 끝이
 * 움직이면 **JC는 길이를 유지한 채 붙어서 따라온다** — 준비시간은 작업에 종속된 구간이라
 * 작업이 늘어난다고 준비시간까지 늘어나지는 않는다(R4, 2026-08-28 확정).
 */
export const resizeGroupBoxes = (boxes = [], mode = RESIZE_RIGHT, edgeTs = 0) => {
  const main = boxes.find(box => box.type !== "jc") ?? boxes[0];
  if (!main) return boxes;

  return boxes.map(box => {
    if (box === main) return mode === RESIZE_LEFT ? { ...box, s: edgeTs } : { ...box, e: edgeTs };

    const length = box.e - box.s;
    const isPrev = String(box.id ?? "").endsWith("_PREVJC");
    if (mode === RESIZE_LEFT && isPrev) return { ...box, e: edgeTs, s: edgeTs - length };
    if (mode === RESIZE_RIGHT && !isPrev) return { ...box, s: edgeTs, e: edgeTs + length };
    return box;
  });
};

/**
 * 커서 시각 → 리사이즈 후 **본 활동의 새 끝점**(스냅·클램프 적용) + 판정.
 *
 * **좌우 모두 민다** — 이웃에 닿으면 그 이웃을 밀어내며 계속 늘어나고, 계획 구간 끝에서 멈춘다.
 *
 * ⚠️ 2026-09-08까지는 **좌측만 선행 박스 끝에서 clamp**했다. `applyShrink`가 뒤쪽으로만 밀 수 있어서,
 * 시작을 앞으로 당겨 선행과 겹치면 스윕이 **리사이즈 대상 자체를 오른쪽으로 되밀어** 방금 한
 * 리사이즈가 취소됐기 때문이다. 지금은 호출부(`AdjustmentGantt`)가 좌측 리사이즈에 **양방향으로 미는
 * `applySlide`**를 쓰므로 그 제약이 사라졌다 — 좌우 동작을 맞춰 달라는 사용자 요청으로 정렬했다.
 * 여기서 clamp하는 건 **계획 구간**뿐이다.
 *
 * `mainStart`/`mainEnd`는 **드래그 시작 시점의 본 활동 양 끝**이다(움직이지 않는 쪽이 기준이 된다).
 *
 * 스냅은 **움직이지 않는 쪽 끝을 기준으로 한 변위 스냅**이다 — 좌측 엣지는 `mainStart`, 우측 엣지는
 * `mainEnd`가 기준점이다. 그래서 **길이가 정확히 1시간 배수로만 변하고 반대쪽 끝의 분·초·ms는
 * 손대지 않는다.** 하한·상한도 격자점으로 내려/올려 잡아(`latticeFloor`/`latticeCeil`) clamp가
 * 격자를 깨지 않게 한다 — 그냥 clamp하면 `끝 - 1시간` 같은 값이 나와 분·초가 다시 어긋난다.
 *
 * ⚠️ **계획 구간 벽만 예외로 정확히 붙인다**(2026-09-10, §7-32). 벽이 격자 위에 없으면 격자점으로는
 * 벽에 닿지 못해서 **끌어도 거기서 막혔다** — 신고 사례는 종료가 `:13:02`인 작업의 우측 엣지가
 * 계획 끝(18:00) 대신 `17:13:02`에서 멈춰 **46분 58초를 못 채웠다**. 부족량은 `분:초`에서 나오므로
 * **종료(또는 시작)가 정시인 작업만 멀쩡했고**, 그래서 "일부 라인에서만" 보였다.
 * 마지막 한 칸에서만 격자를 벗어나며, `runClamped`가 이미 쓰는 규칙과 같은 계열이다
 * (§7-29 규칙 5 "제약에 걸리면 튕기지 말고 벽에 붙인다").
 *
 * **최소 길이 벽은 그대로 격자에 둔다** — 성격이 다른 안전장치이고, 60~119분 작업의 거동이 바뀐다
 * (좌측 엣지로 한 칸도 못 줄이는 현행 동작이 유지된다). 2026-09-10 사용자 확정.
 */
export const resolveResizeEdge = ({
  units = [],
  movingKey = null,
  mode = RESIZE_RIGHT,
  ts = null,
  mainStart = 0,
  mainEnd = 0,
  bound = null,
  snap = SNAP_MS,
  minDuration = MIN_DURATION_MS
}) => {
  const group = units.find(unit => unit.key === movingKey);
  if (!group || ts == null || !Number.isFinite(ts)) return { status: DROP_BLOCKED, edgeTs: null };

  const others = units.filter(unit => unit.key !== movingKey);
  // 기준점 = **움직이지 않는 쪽 끝**. 좌측 엣지를 끌면 끝(mainEnd)이 고정이므로 시작을 mainStart
  // 격자에서 옮기고, 우측 엣지면 반대다.
  const anchor = mode === RESIZE_LEFT ? mainStart : mainEnd;
  let edge = anchor + snapTs(ts - anchor, snap);

  if (mode === RESIZE_LEFT) {
    // 그룹 좌측 여유분(= prevJC 길이) — 본 활동 시작을 당기면 JC가 그만큼 더 왼쪽으로 나간다.
    const lead = mainStart - group.s;

    // 하한·상한을 **격자점으로** 잡는다 — 그냥 clamp하면 `mainEnd - 1시간`처럼 격자를 벗어난 값이
    // 나와 분·초가 어긋난다. 예: 길이 90분 작업의 좌측 엣지는 1시간 줄이면 최소길이(1시간) 위반이라
    // **아예 못 줄어든다**(허용 격자점이 없다) — 최소 길이 쪽은 그대로 둔다(위 주석).
    const wall = (bound?.startTs ?? -Infinity) + lead;   // 계획 시작이 허용하는 가장 이른 시작
    const floor = latticeCeil(anchor, wall, snap);       // 그 벽보다 이르지 않은 첫 격자점
    const ceiling = latticeFloor(anchor, mainEnd - minDuration, snap); // 최소 길이를 지키는 마지막 격자점

    if (floor > ceiling) return { status: DROP_BLOCKED, edgeTs: null };

    // 마지막 격자점보다 더 왼쪽을 원하면 **벽에 정확히** 붙인다(위 ⚠️). 벽이 격자 위에 있으면
    // `wall === floor` 라 동작이 그대로다 — 정시 시작인 작업은 원래도 정확히 닿았다.
    const edgeTs = edge < floor ? wall : Math.min(edge, ceiling);
    // 선행을 침범하면 밀림이다 — 우측이 후속을 침범할 때와 같은 판정이다.
    const prevEnd = Math.max(-Infinity, ...others.filter(unit => unit.e <= group.s).map(unit => unit.e));
    return { status: edgeTs - lead < prevEnd ? DROP_PUSH : DROP_FREE, edgeTs };
  }

  const tail = group.e - mainEnd;                   // nextJC 길이

  // 좌측과 **같은 방식으로 격자 clamp**한다 — 계획 끝을 넘으면 거부가 아니라 넘지 않는 마지막
  // 격자점까지만 늘린다(2026-09-08: 거부하면 벽 근처에서 리사이즈가 통째로 안 먹는다).
  const floor = latticeCeil(anchor, mainStart + minDuration, snap);
  const wall = bound ? bound.endTs - tail : Infinity;    // 계획 끝이 허용하는 가장 늦은 종료
  const ceiling = bound ? latticeFloor(anchor, wall, snap) : Infinity;

  if (floor > ceiling) return { status: DROP_BLOCKED, edgeTs: null };

  // 좌측의 거울상 — 마지막 격자점보다 더 오른쪽을 원하면 **벽에 정확히** 붙인다.
  edge = edge > ceiling ? wall : Math.max(edge, floor);

  const nextStart = Math.min(
    Infinity,
    ...others.filter(unit => unit.s >= group.e).map(unit => unit.s)
  );
  return { status: edge + tail <= nextStart ? DROP_FREE : DROP_PUSH, edgeTs: edge };
};

/**
 * old `attachActivity` 이식 — 선택 작업을 **이웃에 붙인다**(왼쪽=선행 끝 / 오른쪽=후속 시작 - duration).
 * 이웃이 없으면 계획 구간의 끝에 붙는다(old도 `GanttBase`/`GanttFinish`로 폴백했다).
 * 반환은 새 시작시각. 이미 붙어 있으면 `null`(호출부가 FP_MSG_WARN_MOVE_ANYMORE를 띄운다).
 */
export const attachStart = ({ units = [], movingKey = null, direction = "left", bound = null }) => {
  const index = units.findIndex(unit => unit.key === movingKey);
  if (index === -1) return null;

  const moving = units[index];
  const duration = moving.e - moving.s;

  if (direction === "left") {
    const target = units[index - 1];
    const start = target ? target.e : (bound?.startTs ?? moving.s);
    return start === moving.s ? null : start;
  }

  const target = units[index + 1];
  const end = target ? target.s : (bound?.endTs ?? moving.e);
  const start = end - duration;
  return start === moving.s ? null : start;
};

// (옛 `MOVE_UNIT_MS` 제거 — old의 이동 단위 선택(Hour/Minute × 1~30)을 없애고 `SNAP_MS` 하나로
//  통일했다. ×2 버튼만 `SNAP_MS * 2`를 쓴다. 2026-08-28 사용자 결정, §7-15e)
